import { AxiosInstance } from 'axios'
import _pick from 'lodash/pick'
import { IMinimalContribution } from '../../../types/contribution.types'
import {
  IUser,
  IFollow,
  IExpertiseTag,
  IOAuthClient,
  IGetUsersResponse,
  IGetUsersParams,
  IUserSettings,
  IUserActivity,
  IUserFollowing,
  ISettingsParams
} from '../../../types/user.types'
import { IApprovalGroupOfUser } from '../../../types/solutionApproval.types'
import { IBadgeFamilyRaw } from '../../../types/badges.types'
import { IUsersKnowledgeSpacesResponse } from '../../../types/knowledgeSpaces.types'
import { IEmailNotificationChannelSettings, INotificationChannelSettings } from '../../../types/notifications.types'
import { IPaginatedApiResp } from '../../../types/api.types'
import { IAnnouncementComplete } from '../../../types/announcements.types'
import { IExpiredAnswersResponse } from '../../../types/solution.types'
import { createOptimizedAPI } from '../utils'

export interface IExpertiseTagsResponse {
  tags: IExpertiseTag[];
  approximate_total: number;
}

export interface IUsersApiFactoryOptions {
  overrideFirstSeenForCurrentUser: boolean
}

export function usersApiFactory(api: AxiosInstance, options?: IUsersApiFactoryOptions) {
  const baseUrl = '/api/v1/users'
  const methods = {
    async getCurrentUser() {
      const response = await api.get<IUser>(`${baseUrl}/me`)
      if (options?.overrideFirstSeenForCurrentUser) {
        return {
          ...response,
          data: {
            ...response.data,
            first_seen: new Date().toISOString()
          }
        }
      }
      return response
    },
    getConceptExperience(globalConceptId: string) {
      return api.get<IMinimalContribution[]>(`${baseUrl}/me/concept-experience/${globalConceptId}`)
    },
    getExpiredAnswers() {
      return api.get<IExpiredAnswersResponse>(`${baseUrl}/me/solutions/expired`)
    },
    getUser(userId: number|string, deanonymize_deleted_users = false, deanonymize_users_email = false) {
      const params : { deanonymize_deleted_users?: boolean, deanonymize_users_email?: boolean } = {}
      if (deanonymize_deleted_users) {
        params.deanonymize_deleted_users = true
      }
      if (deanonymize_users_email) {
        params.deanonymize_users_email = true
      }
      return api.get<IUser>(`${baseUrl}/${userId.toString()}`, {
        params
      })
    },
    getAnnouncements() {
      return api.get<IAnnouncementComplete[]>(`${baseUrl}/me/announcements`)
    },
    getUsers(params: IGetUsersParams = {}) {
      const requestParams: any = _pick(params, [ 'query', 'limit', 'offset', 'sort' ])

      if (params.deanonymizeUsersEmail) {
        requestParams.deanonymize_users_email = true
      }

      const include = [
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ...(params.deleted ? [ 'deleted' ] : []),
        ...(params.technical ? [ 'technical' ] : [])
      ]
      if (include.length) {
        requestParams.include = include.join(',')
      }

      if (params.filter !== 'all') {
        requestParams.filter = params.filter
      }

      return api.get<IGetUsersResponse>(baseUrl, {
        params: requestParams
      })
    },
    isFollowingUser(userId: number) {
      return api.get<IFollow>(`${baseUrl}/${userId}/follow`)
    },
    unfollow(userId: number) {
      return api.delete<IFollow>(`${baseUrl}/${userId}/follow`)
    },
    follow(userId: number) {
      return api.put<IFollow>(`${baseUrl}/${userId}/follow`)
    },
    getOAuthClients(userId: number) {
      return api.get<IOAuthClient[]>(`${baseUrl}/${userId}/oauth-clients`)
    },
    getRoles() {
      return api.get<string[]>(`${baseUrl}/me/roles/evaluated`)
    },
    getSolutionAuthorities() {
      return api.get<IApprovalGroupOfUser[]>(`${baseUrl}/me/solution-authorities`)
    },
    setEmailSettings(settings: Partial<IEmailNotificationChannelSettings>) {
      return api.put<IEmailNotificationChannelSettings>(`${baseUrl}/me/settings/communication/email`, settings)
    },
    setWebPushSettings(settings: Partial<INotificationChannelSettings>) {
      return api.put<INotificationChannelSettings>(`${baseUrl}/me/settings/communication/push/web`, settings)
    },
    updateSettings(settings: Partial<ISettingsParams>) {
      return api.put<IUserSettings>(`${baseUrl}/me/settings`, settings)
    },
    updateOtherUserSettings(userId: number, settings: Partial<ISettingsParams>) {
      return api.put<IUserSettings>(`${baseUrl}/${userId}/settings`, settings)
    },
    getEmailSettings() {
      return api.get<IEmailNotificationChannelSettings>(`${baseUrl}/me/settings/communication/email`)
    },
    getWebPushSettings() {
      return api.get<INotificationChannelSettings>(`${baseUrl}/me/settings/communication/push/web`)
    },
    getSettings() {
      return api.get<IUserSettings>(`${baseUrl}/me/settings`)
    },
    getOtherUserSettings(userId: number) {
      return api.get<IUserSettings>(`${baseUrl}/${userId}/settings`)
    },
    getBadges(userId: number) {
      return api.get<{ badges: IBadgeFamilyRaw[] }>(`${baseUrl}/${userId}/badges`)
    },
    getKnowledgeSpaces() {
      return api.get<IUsersKnowledgeSpacesResponse>(`${baseUrl}/me/knowledge-spaces`)
    },
    joinKnowledgeSpace(knowledgeSpaceId: number) {
      return api.put<void>(`${baseUrl}/me/knowledge-spaces/${knowledgeSpaceId}`)
    },
    leaveKnowledgeSpace(knowledgeSpaceId: number) {
      return api.delete<void>(`${baseUrl}/me/knowledge-spaces/${knowledgeSpaceId}`)
    },
    requestKnowledgeSpaceAccess(knowledgeSpaceId: number) {
      return api.put<void>(`${baseUrl}/me/knowledge-spaces/${knowledgeSpaceId}/membership-request`)
    },
    revokeKnowledgeSpaceAccessRequest(knowledgeSpaceId: number) {
      return api.delete<void>(`${baseUrl}/me/knowledge-spaces/${knowledgeSpaceId}/membership-request`)
    },
    getAskNicelyEmailHash(userId: number) {
      return api.get<{ email_hash: string }>(`${baseUrl}/${userId}/asknicely`)
    },
    getUserActivity(userId: number, limit = 20, offset = 0) {
      return api.get<IPaginatedApiResp<IUserActivity[]>>(`${baseUrl}/${userId || 'me'}/activities?limit=${limit}&offset=${offset}`)
    },
    getFollowing(userId: number, offset = 0, limit = 30) {
      return api.get<IPaginatedApiResp<IUserFollowing[]>>(`${baseUrl}/${userId}/following`, {
        params: { offset, limit }
      })
    },
    getFollowingSuggestions(userId: number, limit = 2) {
      return api.get<IUser[]>(`${baseUrl}/${userId}/subjects-to-follow`, {
        params: { limit }
      })
    },
    getRecentRecommendations(knowledgeSpaceId?: number, limit = 3) {
      return api.get<IUser[]>(`${baseUrl}/me/users-i-recommended`, {
        params: { limit, knowledge_space: knowledgeSpaceId }
      })
    },
    getRecentMentions(knowledgeSpaceId?: number, limit = 3) {
      return api.get<IUser[]>(`${baseUrl}/me/users-i-mentioned`, {
        params: { limit, knowledge_space: knowledgeSpaceId }
      })
    },
    getRecentShares(knowledgeSpaceId?: number, limit = 3) {
      return api.get<IUser[]>(`${baseUrl}/me/users-i-shared-with`, {
        params: { limit, knowledge_space: knowledgeSpaceId }
      })
    }
  }
  return createOptimizedAPI(methods)
}
